/**
 * src/pages/LandingPage.tsx
 * =============================================================================
 * Landing pública. "El editor en estado de espera" (2026-05-24):
 *
 * En vez de mostrar un card con dropzone + título "Añade tu PDF", la landing
 * mimetiza el visor: mismo fondo `bg-zinc-100`, toolbar de anotaciones
 * difuminada a la izquierda y botón de ayuda atenuado abajo-derecha. El usuario
 * "ve" que está dentro del editor y que solo le falta el PDF.
 *
 * En el centro un texto suelto (sin card) invita a arrastrar o clicar.
 * Durante la subida sustituye el texto por un porcentaje grande. Eso es todo.
 *
 * Por qué este flujo:
 *  - El editor ES el producto. Mostrarlo (aunque sea vacío) al instante
 *    elimina la fricción "qué es esta app".
 *  - Sin formulario "Para/Mensaje/Enviar" — destinatarios opcionales DESDE
 *    el visor con el botón "Invitar".
 *
 * Coste: subimos un PDF aunque el usuario no comparta. Mitigamos con TTL
 * agresivo en free tier (7d). Ver [[project_monetization]].
 * =============================================================================
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FolderOpen, HelpCircle, Upload } from 'lucide-react'
import i18n from 'i18next'
import { AnnotationToolbar } from '@/components/documento/AnnotationToolbar'
import { FullScreenDropOverlay } from '@/components/landing/FullScreenDropOverlay'
import { HelpModal } from '@/components/documento/HelpModal'
import { LocaleSwitcher } from '@/components/landing/LocaleSwitcher'
import { MisDocumentosDrawer } from '@/components/landing/MisDocumentosDrawer'
import { RotatingWord } from '@/components/landing/RotatingWord'
import { CookiesBanner } from '@/components/legal/CookiesBanner'
import { LegalModal, type LegalDoc } from '@/components/legal/LegalModal'
import { UserMenu } from '@/components/auth/UserMenu'
import { AccountModal } from '@/components/auth/AccountModal'
import { useAuth } from '@/components/providers/AuthProvider'
import { useActiveSpace } from '@/components/providers/ActiveSpaceProvider'
import { useFullScreenDrop } from '@/hooks/useFullScreenDrop'
import { submitShare, SubmitShareError } from '@/services/uploadService'
import { uploadDocumento, UploadDocumentoError } from '@/services/documentoUploadService'
import { isValidPdf } from '@/lib/pdfValidate'
import { formatBytes } from '@/services/billingService'
import { ModalLimite } from '@/components/landing/ModalLimite'
import { esMotivoDeLimite, type MotivoLimite } from '@/lib/motivosLimite'
import { DEFAULT_ANNOTATION_COLOR, DEFAULT_STROKE_WIDTH } from '@/types/annotation'

// Límites del backend, replicados en cliente para evitar el round-trip +
// upload parcial cuando el archivo excede. Dos topes distintos:
//  - Anónimo:     100 MB (ver MAX_SIZE_BYTES en submit-share del Worker).
//  - Autenticado: 200 MB (ver MAX_SIZE_BYTES en upload-documento del Worker).
// Antes el cliente aplicaba 100 MB a todos y un usuario logueado no podía
// subir un archivo de 150 MB que el backend sí aceptaba.
const MAX_SIZE_BYTES_ANONIMO = 100 * 1024 * 1024
const MAX_SIZE_BYTES_AUTENTICADO = 200 * 1024 * 1024

type Phase = 'idle' | 'uploading' | 'error'

export function LandingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  // Espacio donde aterrizan las subidas. Lo elige el usuario en el
  // switcher del drawer; si no ha tocado nada, es su espacio personal.
  const { activeSpaceId } = useActiveSpace()
  const [phase, setPhase] = useState<Phase>('idle')
  const [progress, setProgress] = useState(0)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  /**
   * Motivo por el que no se ha podido subir, cuando es un TOPE y no un fallo.
   * Se lleva aparte de `errorCode` justo por eso: un tope no es un error, y
   * mezclarlos es lo que hacía que saliera "algo falló, inténtalo de nuevo".
   */
  const [motivoLimite, setMotivoLimite] = useState<MotivoLimite | null>(null)
  /**
   * Límite del espacio en bytes, tal como lo manda el servidor con el error
   * de cuota. Se guarda en bruto (no formateado) porque el modal lo usa
   * también para elegir el texto: no es lo mismo llenar los 300 MB gratuitos
   * que los 50 GB de un Pro o de un espacio compartido.
   */
  const [quotaLimiteBytes, setQuotaLimiteBytes] = useState<number | undefined>(undefined)
  const inputRef = useRef<HTMLInputElement>(null)
  // Modal legal: cerrado por defecto. Se abre desde los links del footer
  // sin navegar — el usuario nunca pierde el "escritorio" detrás.
  const [legalDoc, setLegalDoc] = useState<LegalDoc | null>(null)
  // Drawer lateral derecho con "Mis documentos". El usuario lo abre desde
  // el botón arriba-derecha o navegando a /mis-documentos (bookmark legacy).
  const [docsOpen, setDocsOpen] = useState(
    () => typeof window !== 'undefined' && window.location.search.includes('docs=1'),
  )
  // Modal de cuenta/facturación. Se abre desde UserMenu → Cuenta.
  const [accountOpen, setAccountOpen] = useState(false)
  // Modal de ayuda con atajos de teclado y consejos del visor. Se abre
  // desde el botón "?" abajo-derecha.
  const [helpOpen, setHelpOpen] = useState(false)
  // Aviso de una sola vez al volver de un flujo EXTERNO a la app. Dos casos:
  //   ?upgraded=1 → vuelta de Polar Checkout tras pagar
  //   ?spaceActivated=1 → vuelta tras activar un espacio compartido
  //   ?storageAdded=1 → vuelta tras ampliar 50 GB el espacio elegido
  //   ?verified=1 → vuelta del enlace de verificación de email
  //
  // El de verificación se añadió el 2026-08-14: el enlace del correo dejaba
  // al usuario en la landing ya logueado pero sin decirle nada, y quedaba la
  // duda de si la verificación había funcionado. Es el primer contacto real
  // con el producto; callar ahí genera desconfianza.
  //
  // Implementación: leemos la query en el inicializador lazy del useState (no
  // en un useEffect con setState dentro, que dispara
  // `react-hooks/set-state-in-effect`). Limpiar la query y auto-cerrar van en
  // un effect aparte, para que el aviso no reaparezca al navegar por dentro.
  const [toast, setToast] = useState<'upgraded' | 'spaceActivated' | 'storageAdded' | 'verified' | null>(() => {
    if (typeof window === 'undefined') return null
    const q = new URLSearchParams(window.location.search)
    if (q.get('upgraded') === '1') return 'upgraded'
    if (q.get('spaceActivated') === '1') return 'spaceActivated'
    if (q.get('storageAdded') === '1') return 'storageAdded'
    if (q.get('verified') === '1') return 'verified'
    return null
  })
  useEffect(() => {
    if (!toast) return undefined
    window.history.replaceState({}, '', window.location.pathname)
    const id = window.setTimeout(() => setToast(null), 6000)
    return () => window.clearTimeout(id)
  }, [toast])

  const { isDragging } = useFullScreenDrop({
    accept: 'application/pdf',
    onFile: (f) => void startUpload(f),
  })

  async function startUpload(file: File) {
    if (phase === 'uploading') return
    if (file.type !== 'application/pdf') {
      setErrorCode('invalid_content_type')
      setPhase('error')
      return
    }
    // El tope depende de si hay sesión: el flujo anónimo (submit-share) admite
    // menos que el autenticado (upload-documento).
    if (file.size > (user ? MAX_SIZE_BYTES_AUTENTICADO : MAX_SIZE_BYTES_ANONIMO)) {
      setErrorCode('file_too_large')
      setPhase('error')
      return
    }
    // Magic number check: rechaza archivos con extensión .pdf falsa (un
    // .xlsx renombrado, una imagen, etc.). Evita gastar R2 y mostrar
    // "Invalid PDF structure" en el visor más tarde. (Validación
    // defensiva en backend pendiente — feedback_ux_polish 2026-05-27.)
    if (!(await isValidPdf(file))) {
      setErrorCode('invalid_content_type')
      setPhase('error')
      return
    }
    setPhase('uploading')
    setProgress(0)
    setErrorCode(null)
    try {
      // Routing por estado de auth:
      //  - Logueado → sube al ESPACIO ACTIVO del switcher (que puede ser el
      //    personal o uno compartido). Endpoint /upload-documento crea fila
      //    en documentos + versiones, aterriza en /d/:documentoId.
      //  - Anónimo  → flujo de share clásico WeTransfer. /submit-share
      //    crea fila en waitlist, aterriza en /s/:token.
      if (user) {
        const result = await uploadDocumento({
          file,
          nombreDocumento: file.name,
          // ⚠️ Sin esto el documento caía SIEMPRE en el espacio personal,
          // aunque el usuario tuviera seleccionado el espacio de su estudio.
          // Era lo que hacía inservible el producto de espacios compartidos:
          // se cobraban 5 €/mes por un espacio que no podía recibir un solo
          // archivo, y el trabajo del equipo acababa en el espacio privado de
          // quien lo subía, consumiendo su cuota personal.
          // Detectado en la auditoría del 2026-08-21.
          organizationId: activeSpaceId ?? undefined,
          onProgress: setProgress,
        })
        navigate(`/d/${result.documentoId}`)
      } else {
        const result = await submitShare({
          file,
          locale: i18n.resolvedLanguage ?? 'es',
          onProgress: setProgress,
        })
        navigate(`/s/${result.waitlistId}`)
      }
    } catch (err) {
      let code = 'generic'
      let detalles: Record<string, unknown> | undefined
      if (err instanceof SubmitShareError || err instanceof UploadDocumentoError) {
        code = err.code
        detalles = err.details
      }

      // Llegar a un tope NO es un error: la persona no ha hecho nada mal y no
      // puede arreglarlo reintentando. Hasta ahora estos casos caían en el
      // "algo falló, inténtalo de nuevo" genérico, que es justo lo peor que se
      // puede decir aquí. Se explican en su propio modal.
      if (esMotivoDeLimite(code)) {
        // El servidor manda su límite en los detalles cuando el motivo es la
        // cuota. Se usa para decir "has ocupado los 300 MB" en vez de una
        // frase vaga: la cifra concreta es lo que hace que el mensaje se
        // entienda de una lectura.
        // Se fija SIEMPRE (también a undefined): si no, un segundo intento
        // sin detalles arrastraría la cifra del anterior.
        const limite = detalles?.bytesLimit
        setQuotaLimiteBytes(typeof limite === 'number' ? limite : undefined)
        setMotivoLimite(code)
        setPhase('idle')
        return
      }

      setErrorCode(code)
      setPhase('error')
    }
  }

  function reset() {
    setPhase('idle')
    setProgress(0)
    setErrorCode(null)
  }

  const isUploading = phase === 'uploading'

  return (
    <div className="fixed inset-0 bg-zinc-100">
      {/* Aviso arriba-centro unos segundos tras pagar o verificar el email.
          La URL ya quedó limpia desde el useEffect. */}
      {toast && (
        <div
          role="status"
          className="absolute top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg bg-zinc-900 text-white text-sm shadow-lg max-w-[calc(100vw-2rem)] text-center"
        >
          {toast === 'upgraded'
            ? t('landing.toast.upgraded', '🎉 ¡Bienvenido a Pro! Ya tienes tu espacio ampliado.')
            : toast === 'spaceActivated'
              ? t('landing.toast.spaceActivated')
              : toast === 'storageAdded'
                ? t('landing.toast.storageAdded')
                : t('landing.toast.verified', '✅ Email verificado. Tu cuenta ya está activa.')}
        </div>
      )}
      {/* Input oculto para click-to-pick. Atado al texto central. */}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void startUpload(f)
          e.target.value = ''
        }}
      />

      {/*
       * Toolbar atenuada: visual de "ya estás en el editor". No clickable.
       * IMPORTANTE: NO usar `blur` aquí — los filtros CSS crean containing
       * block para descendientes absolutos, y romperían el `top-1/2` del
       * propio toolbar (haciéndolo aparecer pegado al wrapper, no al viewport).
       * `opacity-30` solo da el efecto atenuado sin alterar el layout.
       */}
      {/* Toolbar decorativa solo en md+. En mobile no aporta (el visor real
          tampoco se ve en mobile sin scroll) y robaba el espacio del CTA
          central, que es la única acción importante en la landing.
          IMPORTANTE: pasamos no-op para TODOS los callbacks opcionales
          (onDownload, onAddVersion, onUploadAnnotationImage) para que los
          botones aparezcan y la silueta de la toolbar coincida con la
          real del visor — si no, el usuario nota que faltan iconos al
          arrastrar un PDF y "entrar" al editor. */}
      <div
        className="pointer-events-none opacity-30 select-none hidden md:block"
        aria-hidden
      >
        <AnnotationToolbar
          tool="select"
          onToolChange={() => {}}
          color={DEFAULT_ANNOTATION_COLOR}
          onColorChange={() => {}}
          strokeWidth={DEFAULT_STROKE_WIDTH}
          onStrokeWidthChange={() => {}}
          fillShapes={false}
          onFillShapesChange={() => {}}
          visible={true}
          onVisibilityToggle={() => {}}
          canUndo={false}
          onUndo={() => {}}
          selectedAnnotation={null}
          onUpdateSelected={() => {}}
          onDeleteSelected={() => {}}
          onAddVersion={() => {}}
          onDownload={() => {}}
        />
      </div>

      {/* Brand top-left — el único elemento "real" arriba además del login. */}
      <Link
        to="/"
        className="absolute top-4 left-4 z-20 text-sm font-bold tracking-tight text-zinc-900 px-2 h-8 leading-8 rounded-md hover:bg-white hover:shadow-sm"
      >
        Ploter
      </Link>

      {/* Top-right chrome — depende de si hay sesión:
           - Anónimo: link "Iniciar sesión".
           - Logueado: botón "Mis documentos" (abre drawer lateral) + avatar. */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
        {user ? (
          <>
            {/* En mobile mostramos solo el icono (sin texto) para que el
                chrome top-right no compita con el brand de arriba-izquierda.
                Touch target sigue siendo 40x40 (suficiente según Apple HIG). */}
            <button
              type="button"
              onClick={() => setDocsOpen(true)}
              aria-label={t('panel.titulo')}
              title={t('panel.titulo')}
              // Hover perceptible: además de pasar a blanco puro, oscurece el
              // borde y añade sombra. Antes solo iba de `white/80` a `white`
              // sobre fondo zinc-100 — un cambio que no se apreciaba y hacía
              // dudar de si el botón estaba activo.
              className="inline-flex items-center justify-center sm:gap-1.5 h-10 w-10 sm:w-auto sm:h-8 sm:px-3 rounded-md bg-white/80 border border-zinc-200 text-xs font-medium text-zinc-700 hover:text-zinc-900 hover:bg-white hover:border-zinc-300 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300"
            >
              <FolderOpen className="h-4 w-4 sm:h-3.5 sm:w-3.5" strokeWidth={2} />
              <span className="hidden sm:inline">{t('panel.titulo')}</span>
            </button>
            <UserMenu onOpenAccount={() => setAccountOpen(true)} />
          </>
        ) : (
          <Link
            to="/login"
            className="text-sm text-zinc-700 hover:text-zinc-900 px-2 h-8 leading-8 rounded-md hover:bg-white hover:shadow-sm"
          >
            {t('landing.signin')}
          </Link>
        )}
      </div>

      {/* Botón ayuda abajo-derecha. Antes era decorativo desactivado
          (parte de la "ilusión del editor"). Ahora abre el HelpModal con
          los atajos del visor. Visible también en mobile — la ayuda es
          útil en cualquier viewport. */}
      <button
        type="button"
        onClick={() => setHelpOpen(true)}
        aria-label={t('actions.ayudaAtajos')}
        title={t('actions.ayudaAtajos')}
        className="absolute bottom-[calc(1rem+var(--espacio-banner,0px))] right-4 z-10 h-10 w-10 rounded-full bg-white border border-zinc-200 shadow-md flex items-center justify-center text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 hover:border-zinc-300 transition-colors"
      >
        <HelpCircle className="h-5 w-5" strokeWidth={1.8} />
      </button>

      {/* Centro: texto suelto sobre fondo, o porcentaje gigante durante subida. */}
      <div className="absolute inset-0 flex items-center justify-center px-6">
        {isUploading ? (
          <UploadingIndicator progress={progress} />
        ) : phase === 'error' && errorCode ? (
          <ErrorMessage
            code={errorCode}
            // `limit` alimenta la interpolación de `file_too_large` ("supera
            // los {{limit}}"); el resto de claves lo ignoran. El tope se
            // recalcula del estado de sesión — el mismo criterio del check
            // de startUpload.
            messageKey={t(`landing.errors.${errorCode}`, {
              defaultValue: t('landing.errors.generic'),
              limit: formatBytes(user ? MAX_SIZE_BYTES_AUTENTICADO : MAX_SIZE_BYTES_ANONIMO),
            })}
            onRetry={reset}
          />
        ) : (
          <DropCta onClick={() => inputRef.current?.click()} />
        )}
      </div>

      {/* Microcopy + legal al pie — los links abren el modal sin navegar.
          El selector de idioma vive aquí al final para que sea discreto
          (no compite con el upload CTA del centro). */}
      <footer className="absolute bottom-[calc(1rem+var(--espacio-banner,0px))] left-1/2 -translate-x-1/2 z-10 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-zinc-500 max-w-[90vw]">
        <span>{t('landing.microcopy')}</span>
        <span aria-hidden>·</span>
        <LegalFooterLink label={t('common:legal.terms')} onClick={() => setLegalDoc('terms')} />
        <LegalFooterLink label={t('common:legal.privacy')} onClick={() => setLegalDoc('privacy')} />
        <LegalFooterLink label={t('common:legal.cookies')} onClick={() => setLegalDoc('cookies')} />
        <span aria-hidden>·</span>
        <LocaleSwitcher />
      </footer>

      <ModalLimite
        motivo={motivoLimite}
        onClose={() => setMotivoLimite(null)}
        limiteBytes={quotaLimiteBytes}
      />

      <CookiesBanner onMoreInfo={() => setLegalDoc('cookies')} />

      {isDragging && <FullScreenDropOverlay />}

      <LegalModal
        open={legalDoc !== null}
        onClose={() => setLegalDoc(null)}
        doc={legalDoc ?? 'terms'}
      />

      <MisDocumentosDrawer
        open={docsOpen}
        onUpload={() => inputRef.current?.click()}
        onClose={() => {
          setDocsOpen(false)
          // Limpia ?docs=1 si veníamos por URL legacy (/mis-documentos
          // redirige a /?docs=1) para que el back-button del navegador no
          // re-abra el panel.
          if (window.location.search.includes('docs=1')) {
            window.history.replaceState({}, '', window.location.pathname)
          }
        }}
      />

      <AccountModal open={accountOpen} onClose={() => setAccountOpen(false)} />

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}

function LegalFooterLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      // `h-6` (24 px) es el mínimo de objetivo táctil de WCAG 2.5.8 AA. Estos
      // enlaces median 17 px de alto: en un móvil eso es fallar el toque y
      // abrir el de al lado. El texto sigue igual de pequeño; lo que crece es
      // la zona sensible, que es lo que importa para el dedo.
      className="inline-flex items-center h-6 px-0.5 rounded hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
    >
      {label}
    </button>
  )
}

function DropCta({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  // `returnObjects: true` hace que t() devuelva el array tal cual en vez
  // de tratarlo como string. El fallback evita un crash si la clave
  // estuviese mal poblada en algún locale.
  const heroNouns = t('landing.heroNouns', { returnObjects: true, defaultValue: [] }) as unknown
  const nouns = Array.isArray(heroNouns) ? (heroNouns as string[]) : []
  const heroPrefix = t('landing.heroPrefix', { defaultValue: '' })

  return (
    <div className="flex flex-col items-center gap-8 text-center max-w-3xl">
      {/* Hero rotativo. Si no hay nouns en el locale, mostramos solo el prefix. */}
      {heroPrefix && (
        <h1 className="m-0 text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 leading-[1.1]">
          {heroPrefix}{' '}
          {nouns.length > 0 && (
            <RotatingWord words={nouns} className="text-zinc-500" />
          )}
        </h1>
      )}

      {/* CTA de drop — el "click anywhere here to open the picker". */}
      <button
        type="button"
        onClick={onClick}
        className="group flex flex-col items-center gap-2 focus-visible:outline-none"
      >
        <Upload
          className="h-8 w-8 text-zinc-400 group-hover:text-zinc-600 transition-colors"
          strokeWidth={1.4}
        />
        <span className="text-base font-medium text-zinc-800 group-hover:text-zinc-900 transition-colors">
          {t('landing.dropZoneTitle')}
        </span>
        <span className="text-sm text-zinc-500">
          {t('landing.dropZoneHint')}
        </span>
      </button>
    </div>
  )
}

function UploadingIndicator({ progress }: { progress: number }) {
  const pct = Math.round(progress * 100)
  return (
    <div className="flex flex-col items-center gap-4" aria-live="polite">
      <div className="text-5xl font-light text-zinc-800 tabular-nums">{pct}%</div>
      <div className="h-1 w-48 bg-zinc-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-zinc-900 transition-[width] duration-150 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function ErrorMessage({ code, messageKey, onRetry }: { code: string; messageKey: string; onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center gap-3 max-w-md text-center" role="alert">
      <p className="text-sm text-red-600">{messageKey || code}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-xs text-zinc-700 underline underline-offset-4 hover:text-zinc-900"
      >
        {t('landing.errors.retry')}
      </button>
    </div>
  )
}
